<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/20/solid';
import { Constants } from '@/utilities/constants.ts';
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue';
import { useDebounceFn } from '@vueuse/core';
import type { ProjectCarouselImage } from '@/views/projects/types.ts';

// Sans valeur par défaut explicite, Vue ramène une propriété booléenne absente à false,
// ce qui priverait de clavier tout carrousel monté sans préciser la propriété.
const props = withDefaults(
  defineProps<{
    images: ProjectCarouselImage[];
    keyboardEnabled?: boolean;
  }>(),
  { keyboardEnabled: true }
);

const { t } = useI18n();
const containerRef = useTemplateRef('images-container');
const thumbnailsRef = useTemplateRef('thumbnails-container');
const currentIndexRef = ref<number>(0);

const showModal = ref(false);
const originalOverflow = ref('');
const touchStartX = ref(0);
const MAX_QUEUED_TRANSITIONS = 2;
const pendingTransitions = ref(0);
const queuedDelta = ref(0);
const carouselSizes = `(max-width: ${Constants.BreakpointWidths.md}px) 100vw, ${Constants.ImageSizes.Large.Width}px`;

const getSectionCoverImages = () => {
  const { images, ranges } = imageData.value;

  return [...ranges.values()].map((range) => images[range.start]?.src).filter((src): src is string => src !== undefined);
};

const getRemainingImages = () => {
  const { images, ranges } = imageData.value;
  const covers = new Set([...ranges.values()].map((range) => range.start));

  return images.filter((_, index) => !covers.has(index)).map((image) => image.src);
};

const prefetchesEveryImage = () => {
  if (typeof window === 'undefined') return false;

  return window.matchMedia(`(min-width: ${Constants.BreakpointWidths.lg}px)`).matches;
};

const preloadImage = (src: string | undefined) => {
  if (!src) return;

  const img = new window.Image();
  img.sizes = carouselSizes;
  img.srcset = src.toSrcset();
  img.src = src.resizedToExtraLarge();
};

const addImage = (image: { src: string; title: string } | null, position: 'left' | 'center' | 'right') => {
  if (!image) return;

  const container = containerRef.value!;
  const img = document.createElement('img');

  img.sizes = carouselSizes;
  img.srcset = image.src.toSrcset();
  img.src = image.src.resizedToExtraLarge();
  img.alt = image.title || t('views.project.carousel.project');
  img.width = Constants.ImageSizes.ExtraLarge.Width;
  img.height = Constants.ImageSizes.ExtraLarge.Height;
  img.className = 'absolute top-0 left-0 h-full w-full object-cover transition-all rounded-tr-xl rounded-tl-xl duration-[180ms] ease-[cubic-bezier(.25,0,0,1)]';
  img.setAttribute('data-position', position);

  if (position !== 'center') {
    img.loading = 'lazy';
  }

  if (position === 'left') {
    img.style.left = `${-container.offsetWidth}px`;
  } else if (position === 'center') {
    img.style.left = `0px`;
  } else {
    img.style.left = `${container.offsetWidth}px`;
  }

  container.insertBefore(img, container.firstChild);
};

const lazyLoadAbort = ref(false);

const lazyLoadImages = (images: string[], index = 0) => {
  if (lazyLoadAbort.value || index >= images.length || !images[index]) {
    return;
  }

  const img = new window.Image();
  img.sizes = carouselSizes;
  img.srcset = images[index].toSrcset();
  img.src = images[index].resizedToExtraLarge();
  img.onload = img.onerror = () => {
    lazyLoadImages(images, index + 1);
    img.onload = img.onerror = null;
  };
};

const loadCarousel = () => {
  const container = containerRef.value;
  if (!container) {
    return;
  }

  pendingTransitions.value = 0;
  queuedDelta.value = 0;
  currentIndexRef.value = 0;

  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }

  addImage(imageData.value.images.length > 0 ? indexData.value.currentImage : Constants.ProjectNoImageSrc, 'center');

  preloadImage(indexData.value.nextImage?.src);
  preloadImage(indexData.value.previousImage?.src);

  const imagesToCache = prefetchesEveryImage() ? [...getSectionCoverImages(), ...getRemainingImages()] : getSectionCoverImages();
  lazyLoadImages(imagesToCache);
};

function handleTransitionEnd(event: Event) {
  const item = event.target as HTMLImageElement;
  const position = item.getAttribute('data-position') as 'left' | 'center' | 'right';

  item.removeEventListener('transitionend', handleTransitionEnd);

  if (position === 'center') {
    item.parentElement?.removeChild(item);
  } else {
    item.setAttribute('data-position', 'center');
  }

  pendingTransitions.value -= 1;
  if (pendingTransitions.value === 0 && queuedDelta.value !== 0) {
    const nextDirection = queuedDelta.value < 0 ? 'left' : 'right';
    queuedDelta.value += nextDirection === 'left' ? 1 : -1;
    handleClick(nextDirection);
  }
}

const addEventListenersToImages = (direction: 'left' | 'right') => {
  const container = containerRef.value!;
  const moveBy = container.offsetWidth;

  pendingTransitions.value = container.children.length;

  for (let i = 0; i < container.children.length; i++) {
    const img = container.children[i] as HTMLImageElement;
    img.addEventListener('transitionend', handleTransitionEnd, { once: true });

    const position = img.getAttribute('data-position');
    if (position === 'center') {
      img.style.left = direction === 'left' ? `${moveBy}px` : `-${moveBy}px`;
    } else {
      img.style.left = '0px';
    }
  }
};

const addImageAndMove = async (image: { src: string; title: string } | null, direction: 'left' | 'right') => {
  if (!image) return;

  addImage(image, direction);
  await nextTick();
  addEventListenersToImages(direction);
};

const handleClick = async (direction: 'left' | 'right') => {
  if (pendingTransitions.value > 0) {
    const delta = direction === 'left' ? -1 : 1;
    const newDelta = queuedDelta.value + delta;
    queuedDelta.value = Math.max(-MAX_QUEUED_TRANSITIONS, Math.min(MAX_QUEUED_TRANSITIONS, newDelta));
    return;
  }
  let imageToShow;
  let newIndex;
  if (direction === 'left') {
    imageToShow = indexData.value.previousImage;
    newIndex = (currentIndexRef.value - 1 + indexData.value.count) % indexData.value.count;
  } else {
    imageToShow = indexData.value.nextImage;
    newIndex = (currentIndexRef.value + 1) % indexData.value.count;
  }

  await addImageAndMove(imageToShow, direction);
  currentIndexRef.value = newIndex;
};

const debouncedHandleClick = useDebounceFn(
  (direction: 'left' | 'right') => {
    handleClick(direction);
  },
  150,
  { maxWait: 200 }
);

const debouncedMoveLeft = () => debouncedHandleClick('left');
const debouncedMoveRight = () => debouncedHandleClick('right');

const goToImage = async (index: number) => {
  if (pendingTransitions.value > 0 || index === currentIndexRef.value) {
    return;
  }

  const direction = index > currentIndexRef.value ? 'right' : 'left';
  currentIndexRef.value = index;
  await addImageAndMove(indexData.value.currentImage, direction);
};

watch(currentIndexRef, (index) => {
  const container = thumbnailsRef.value;
  if (!container) return;
  const thumbnail = container.children[index] as HTMLElement | undefined;
  if (!thumbnail) return;
  container.scrollTo({ left: thumbnail.offsetLeft - container.clientWidth / 2 + thumbnail.clientWidth / 2, behavior: 'smooth' });
});

watch(showModal, async (open) => {
  if (!open) return;
  await nextTick();
  const container = thumbnailsRef.value;
  const thumbnail = container?.children[currentIndexRef.value] as HTMLElement | undefined;
  if (!container || !thumbnail) return;
  container.scrollTo({ left: thumbnail.offsetLeft - container.clientWidth / 2 + thumbnail.clientWidth / 2 });
});

watch(currentIndexRef, () => {
  preloadImage(indexData.value.previousImage?.src);
  preloadImage(indexData.value.nextImage?.src);
});

const imageData = computed(() => {
  const images = props.images;
  const ranges = new Map<string, { start: number; count: number }>();

  images.forEach((image, index) => {
    const range = ranges.get(image.section);
    if (range) {
      range.count += 1;
    } else {
      ranges.set(image.section, { start: index, count: 1 });
    }
  });

  return { images, ranges };
});

const indexData = computed(() => {
  const images = imageData.value.images;
  const currentIndex = currentIndexRef.value;

  const currentImage = images[currentIndex]
    ? {
        src: images[currentIndex].src,
        title: images[currentIndex].title,
      }
    : null;

  const previousIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
  const previousImage = images[previousIndex]
    ? {
        src: images[previousIndex].src,
        title: images[previousIndex].title,
      }
    : null;

  const nextIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
  const nextImage = images[nextIndex]
    ? {
        src: images[nextIndex].src,
        title: images[nextIndex].title,
      }
    : null;

  const section = images[currentIndex]?.section ?? t('views.project.carousel.project');
  const title = images.length > 0 && images[currentIndex] ? images[currentIndex].title : '';
  const alt = images[currentIndex]?.title || `${section} – ${title}`;

  const range = imageData.value.ranges.get(section);
  const sectionPosition = range ? currentIndex - range.start + 1 : 1;
  const sectionCount = range ? range.count : 1;

  return {
    currentImage,
    previousImage,
    nextImage,
    alt,
    position: currentIndex + 1,
    count: images.length > 0 ? images.length : 1,
    sectionPosition,
    sectionCount,
    section,
    title,
  };
});

const sideImages = computed(() => {
  const images = imageData.value.images;
  if (images.length < 3) {
    return [];
  }

  const result: { index: number; src: string; title: string; section: string }[] = [];
  for (const offset of [1, 2]) {
    const index = (currentIndexRef.value + offset) % images.length;
    const image = images[index];
    if (image) {
      result.push({ index, src: image.src, title: image.title, section: image.section });
    }
  }
  return result;
});

const openModal = () => {
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
};

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && showModal.value) {
    // Empêche le Dialog parent (Headless UI) de se fermer en même temps que la visionneuse
    event.preventDefault();
    event.stopPropagation();
    closeModal();
    return;
  }

  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
    return;
  }

  // Un carrousel désactivé au clavier laisse les flèches à celui qui est au premier plan,
  // sauf lorsque sa propre visionneuse plein écran est ouverte.
  if (!props.keyboardEnabled && !showModal.value) {
    return;
  }

  if (imageData.value.images.length === 0) {
    return;
  }

  const target = event.target;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement || (target instanceof HTMLElement && target.isContentEditable)) {
    return;
  }

  if (event.key === 'ArrowLeft') {
    debouncedMoveLeft();
  } else {
    debouncedMoveRight();
  }
};

const onTouchStart = (event: TouchEvent) => {
  if (!event.touches[0]) return;

  touchStartX.value = event.touches[0].clientX;
};

const onTouchEnd = (event: TouchEvent) => {
  if (!event.changedTouches[0]) return;

  const diff = event.changedTouches[0].clientX - touchStartX.value;
  if (Math.abs(diff) > 50) {
    if (diff > 0) {
      debouncedMoveLeft();
    } else {
      debouncedMoveRight();
    }
  }
};

onMounted(() => {
  loadCarousel();
  // La phase de capture passe avant l'écouteur fenêtre du Dialog Headless UI
  window.addEventListener('keydown', onKeyDown, { capture: true });
});

onUnmounted(() => {
  lazyLoadAbort.value = true;
  window.removeEventListener('keydown', onKeyDown, { capture: true });
});

watch(
  () => props.images,
  () => {
    lazyLoadAbort.value = true;
    nextTick(() => {
      lazyLoadAbort.value = false;
      loadCarousel();
    });
  }
);

watch(showModal, (value) => {
  if (value) {
    originalOverflow.value = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = originalOverflow.value;
  }
});

useHead({
  link: [
    () => {
      const data = indexData.value;
      const src = data.currentImage?.src;
      return src
        ? {
            rel: 'preload',
            as: 'image',
            href: src.resizedToExtraLarge(true),
            imagesrcset: src.toSrcset(true),
            imagesizes: carouselSizes,
          }
        : undefined;
    },
  ],
});
</script>

<template>
  <section aria-roledescription="carousel" class="relative overflow-hidden rounded-xl bg-gray-100" role="group">
    <figure v-if="imageData.images.length > 0 && indexData.currentImage" class="sr-only">
      <img :alt="indexData.alt" :sizes="carouselSizes" :src="indexData.currentImage.src.resizedToSmall()" :srcset="indexData.currentImage.src.toSrcset()" />
      <figcaption aria-live="polite">{{ indexData.section }} – {{ indexData.title }}</figcaption>
    </figure>

    <div :class="sideImages.length > 0 ? 'lg:grid lg:grid-cols-3 lg:gap-2' : ''">
      <div :class="sideImages.length > 0 ? 'lg:col-span-2' : ''" class="relative">
        <button
          v-if="imageData.images.length > 0"
          :aria-label="t('views.project.carousel.previous')"
          class="no-print absolute top-0 left-0 z-10 h-full w-1/6 cursor-pointer rounded-tl-xl bg-black/10 text-white/20 hover:bg-black/20 hover:text-white/40 focus:bg-black/20 focus:text-white/40 md:w-1/12"
          @click="debouncedMoveLeft"
        >
          <ChevronLeftIcon />
        </button>

        <!-- Les pastilles partent du coin gauche et passent par-dessus la barre ombrée de la flèche;
             pointer-events-none laisse les clics atteindre la flèche en dessous. -->
        <div v-if="$slots.default" class="pointer-events-none absolute top-2 right-2 left-2 z-20">
          <slot />
        </div>

        <div
          ref="images-container"
          :class="sideImages.length > 0 ? 'lg:w-full' : ''"
          class="relative aspect-3/2 w-[calc(100%+0.5rem)] cursor-pointer rounded-tl-xl rounded-tr-xl md:max-h-[70vh]"
          @click="openModal"
          @touchstart.passive="onTouchStart"
          @touchend.passive="onTouchEnd"
        />

        <button
          v-if="imageData.images.length > 0"
          :aria-label="t('views.project.carousel.next')"
          class="no-print absolute top-0 right-0 z-10 h-full w-1/6 cursor-pointer rounded-tr-xl bg-black/10 text-white/20 hover:bg-black/20 hover:text-white/40 focus:bg-black/20 focus:text-white/40 md:w-1/12"
          @click="debouncedMoveRight"
        >
          <ChevronRightIcon />
        </button>

        <div v-if="imageData.images.length > 0" class="no-print absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-center rounded-full bg-black/60 px-3 py-2 text-xs text-white md:text-sm lg:text-base">
          <button :aria-label="t('views.project.carousel.previous')" class="mr-1 cursor-pointer text-white/80 hover:text-white focus:text-white" @click.stop="debouncedMoveLeft">
            <ChevronLeftIcon class="size-5" />
          </button>
          <button :aria-label="t('views.project.carousel.next')" class="mr-3 cursor-pointer text-white/80 hover:text-white focus:text-white" @click.stop="debouncedMoveRight">
            <ChevronRightIcon class="size-5" />
          </button>
          <span>{{ indexData.sectionPosition }} / {{ indexData.sectionCount }}</span>
          <span class="mx-3 font-semibold whitespace-nowrap">{{ indexData.section }}</span>
          <span v-if="indexData.title" class="max-w-40 truncate">{{ indexData.title }}</span>
        </div>
      </div>

      <div v-if="sideImages.length > 0" class="no-print relative hidden lg:block">
        <div class="absolute inset-0 flex flex-col gap-2">
          <button v-for="side in sideImages" :key="side.index" :aria-label="side.title || side.section" class="min-h-0 flex-1 cursor-pointer overflow-hidden" @click="goToImage(side.index)">
            <img :alt="side.title || side.section" :src="side.src.resizedToMedium()" class="h-full w-full object-cover" decoding="async" loading="lazy" />
          </button>
        </div>
      </div>
    </div>
  </section>

  <teleport to="body">
    <div v-if="showModal" aria-modal="true" class="fixed inset-0 z-50 flex items-center justify-center bg-black/90" role="dialog" @click.self="closeModal" @touchstart.passive="onTouchStart" @touchend.passive="onTouchEnd">
      <button aria-label="Close" class="absolute top-2 right-2 z-10 cursor-pointer rounded-md p-2 text-3xl font-bold text-white hover:text-gray-300" @click="closeModal">&times;</button>

      <button
        v-if="indexData.count > 1"
        :aria-label="t('views.project.carousel.previous')"
        class="absolute top-1/2 left-2 z-10 -translate-y-1/2 cursor-pointer rounded-full bg-white/10 p-2 text-white hover:bg-white/25 focus:bg-white/25"
        @click="debouncedMoveLeft"
      >
        <ChevronLeftIcon class="size-8" />
      </button>

      <img :alt="indexData.title || indexData.alt" :class="indexData.count > 1 ? 'max-h-[82vh]' : 'max-h-[98vh]'" :src="indexData.currentImage?.src?.resizedToExtraLarge(true)" class="max-w-[98vw] rounded-md shadow-lg" />

      <button
        v-if="indexData.count > 1"
        :aria-label="t('views.project.carousel.next')"
        class="absolute top-1/2 right-2 z-10 -translate-y-1/2 cursor-pointer rounded-full bg-white/10 p-2 text-white hover:bg-white/25 focus:bg-white/25"
        @click="debouncedMoveRight"
      >
        <ChevronRightIcon class="size-8" />
      </button>

      <div v-if="indexData.count > 1" class="absolute inset-x-0 bottom-0 flex flex-col items-center gap-2 pb-3">
        <div class="flex items-center rounded-md bg-black/60 px-3 py-1 text-sm text-white">
          <span>{{ indexData.sectionPosition }} / {{ indexData.sectionCount }}</span>
          <span class="mx-2 font-semibold">{{ indexData.section }}</span>
          <span>{{ indexData.title }}</span>
        </div>
        <div ref="thumbnails-container" class="flex max-w-full gap-2 overflow-x-auto px-4 pb-1">
          <button
            v-for="(image, index) in imageData.images"
            :key="index"
            :aria-label="image.title || image.section"
            :class="index === currentIndexRef ? 'border-ph-green-600' : 'border-transparent opacity-60 hover:opacity-100 focus:opacity-100'"
            class="aspect-3/2 h-14 shrink-0 cursor-pointer overflow-hidden rounded-md border-2"
            @click="goToImage(index)"
          >
            <img :alt="image.title || image.section" :src="image.src.resizedToSmall()" class="h-full w-full rounded-sm object-cover" decoding="async" loading="lazy" />
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style lang="css" scoped></style>
